import React, { useState, useEffect } from "react";
import BaseModal from "./BaseModal"; // Verify the import path
import LabelChip from "../components/LabelChip";
import { base64ToImage } from "../utils/VerbatimUtils"
import "./FocusEditingModal.css";

const FocusEditingModal = ({
  show,
  verbatims,
  labelType,
  label,
  isSentimentEnabled,
  sentimentState, // Add sentimentState as a prop
  action,
  onClose,
  onApplyLabel,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedSentimentState, setSelectedSentimentState] = useState(
    sentimentState || "" // Initialize with the sentimentState prop
  );

  useEffect(() => {
    if (show) {
      setSelectedSentimentState(sentimentState || ""); // Reset state when modal opens
    }
  }, [show, sentimentState]);

  const currentVerbatim = verbatims?.[currentIndex];
  const isLastVerbatim = currentIndex === verbatims.length - 1;

  const progressText = `Progress: ${currentIndex + 1} / ${verbatims.length}`;

  const handleApply = () => {
    if (currentVerbatim) {
      onApplyLabel(currentVerbatim.id, labelType, label, selectedSentimentState, action);

      if (isLastVerbatim) {
        onClose(); // Close the modal if it's the last verbatim
      } else {
        goToNext();
      }
    }
  };

  const handleSkip = () => {
    if (isLastVerbatim) {
      onClose(); // Close the modal if it's the last verbatim
    } else {
      goToNext(); // Move to the next verbatim
    }
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex < verbatims.length - 1 ? prevIndex + 1 : prevIndex
    );
    setSelectedSentimentState(sentimentState || ""); // Reset sentiment state for next verbatim
  };

  return (
    <BaseModal
      show={show}
      title={progressText} // Show progress instead of static title
      onClose={onClose}
      primaryActionLabel={isLastVerbatim ? "Apply & Close" : "Apply Label"}
      secondaryActionLabel={isLastVerbatim ? "Skip & Close" : "Skip"}
      onConfirm={handleApply}
      onSecondaryAction={handleSkip}
    >
      {currentVerbatim ? (
        <div className="focus-editing-modal-body">
          <div className="verbatim-box">
            {typeof currentVerbatim.verbatim === 'string' && <p>{currentVerbatim.verbatim}</p>}
            {Array.isArray(currentVerbatim.verbatim) && currentVerbatim.verbatim.map((item, index) => {
                if (item.type === 'text' && item.content.trim() !== '') {
                    return <span key={index}>{item.content}</span>;
                } else if (item.type === 'image' && item.content) {
                    return (
                        <div key={index} className="review-image-block">
                            <img 
                                src={base64ToImage(item.content)} 
                                alt="Verbatim" 
                                className="review-verbatim-image"
                                onError={(e) => {
                                console.error("Erreur de chargement de l'image", e);
                                e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg==";
                                }}
                            />
                        </div>
                    );
                }
                return null;
            })}
          </div>

          <div className="existing-labels">
            <div className="label-groups-container">
              {Object.entries(currentVerbatim.labels || {}).map(([type, labels]) => (
                <div key={type} className="label-type-box">
                  <strong>{type}:</strong>
                  <div className="label-chips-container">
                    {labels.map((label) => (
                      <LabelChip
                        key={label.name}
                        type={type}
                        name={label.name}
                        state={label.state}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {isSentimentEnabled && (
            <div className="sentiment-buttons">
              {["positive", "negative", "mixed"].map((sentiment) => (
                <button
                  key={sentiment}
                  className={`modal-sentiment-button ${
                    selectedSentimentState === sentiment ? "active" : ""
                  } ${sentiment}`}
                  onClick={() => setSelectedSentimentState(sentiment)}
                >
                  {sentiment.charAt(0).toUpperCase() + sentiment.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p>No more verbatims to process.</p>
      )}
    </BaseModal>
  );
};

export default FocusEditingModal;
